/**
 * A very small Markdown renderer for the guidelines page.
 *
 * Deliberately not a library: the guidelines use a fixed, known subset (headings,
 * tables, lists, blockquotes, bold, inline code), and the content is authored by an
 * admin rather than by arbitrary users. Everything is rendered as React elements, so
 * there is no `dangerouslySetInnerHTML` and no HTML injection path.
 */

import { Fragment } from 'react';

/** Bold, inline code and italics inside a line of text. */
function inline(text, keyPrefix = '') {
  const parts = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g;
  let lastIndex = 0;
  let match;
  let i = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const token = match[0];
    const key = `${keyPrefix}-${i++}`;
    if (token.startsWith('**')) parts.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    else if (token.startsWith('`')) parts.push(<code key={key}>{token.slice(1, -1)}</code>);
    else parts.push(<em key={key}>{token.slice(1, -1)}</em>);
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

const splitRow = (line) =>
  line
    .trim()
    .replace(/^\||\|$/g, '')
    .split('|')
    .map((cell) => cell.trim());

export function renderMarkdown(source) {
  const lines = String(source || '').split('\n');
  const blocks = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i += 1;
      continue;
    }

    // Heading
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const Tag = `h${Math.min(level + 1, 5)}`;
      blocks.push(<Tag key={key++}>{inline(heading[2], `h${key}`)}</Tag>);
      i += 1;
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      blocks.push(<hr key={key++} />);
      i += 1;
      continue;
    }

    // Table
    if (line.includes('|') && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|/.test(lines[i + 1])) {
      const header = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
        rows.push(splitRow(lines[i]));
        i += 1;
      }
      blocks.push(
        <div className="table-wrap" key={key++}>
          <table className="data">
            <thead>
              <tr>
                {header.map((cell, c) => (
                  <th key={c}>{inline(cell, `th${c}`)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c}>{inline(cell, `td${r}-${c}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Blockquote
    if (line.trimStart().startsWith('>')) {
      const buffer = [];
      while (i < lines.length && lines[i].trimStart().startsWith('>')) {
        buffer.push(lines[i].replace(/^\s*>\s?/, ''));
        i += 1;
      }
      blocks.push(
        <blockquote key={key++}>
          {buffer.map((text, n) => (
            <Fragment key={n}>
              {inline(text, `bq${n}`)}
              {n < buffer.length - 1 && ' '}
            </Fragment>
          ))}
        </blockquote>,
      );
      continue;
    }

    // List
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ''));
        i += 1;
      }
      blocks.push(
        <ul key={key++}>
          {items.map((item, n) => (
            <li key={n}>{inline(item, `li${n}`)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    // Paragraph
    const buffer = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^(#{1,4})\s/.test(lines[i]) &&
      !lines[i].trimStart().startsWith('>') &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^---+$/.test(lines[i].trim())
    ) {
      buffer.push(lines[i]);
      i += 1;
    }
    blocks.push(<p key={key++}>{inline(buffer.join(' '), `p${key}`)}</p>);
  }

  return blocks;
}
